import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import { GlobeIcon, PersonIcon } from "./../../assets/IconsSvg";
import React, { FC, useContext, useEffect, useState } from "react";

import ChangeLanguageModal from "../dialogs/ChangeLanguageModal";
import { LanguageContext } from "common/contexts/LanguageContext";
import LinkType from "common/constants/LinkType";
import theme from "common/theme/theme";
import { useAuthContext } from "common/contexts/AuthContext";
import useLinkStore from "common/store/LinksStore";
import { useAuth0, User } from "@auth0/auth0-react";

interface IProps {
  fullWidth?: boolean;
}

const Header: FC<IProps> = (props: IProps) => {
  const { selectedLanguageLocale } = useContext(LanguageContext);
  const { getFilteredLinks } = useLinkStore();
  const linkState = useLinkStore.getState();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [initials, setInitials] = useState<string | undefined>();
  const [primaryName, setPrimaryName] = useState<string | undefined>();
  const [isLanguageDialogOpen, setIsLanguageDialogOpen] = useState<boolean>(false);
  const { getString } = useContext(LanguageContext);
  const { isAuthenticatedUser, userInfo } = useAuthContext();

    const headerLinks = getFilteredLinks(LinkType.Header)(linkState);

    const { loginWithRedirect, logout } = useAuth0(); // Use Auth0's isAuthenticated property


  const formatLocale = (locale: string) => {
    var parts = locale.split('-');
    return `${parts[1]}/${parts[0]}`;
  };

  useEffect(() => {
      if (userInfo) {
          setPrimaryName(userInfo.givenName);
          setInitials(`${userInfo.givenName?.charAt(0)}${userInfo.familyName?.charAt(0)}`.toUpperCase());
      }
  }, [userInfo]);

  useEffect(() => {}, [selectedLanguageLocale]);

  const handleUserOptionClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

    const handleLogin = async () => loginWithRedirect({
        authorizationParams: {
            'ext-returnPath': '/en-us/',
            'ext-returnuri': 'https://q.wirelessplanning.emerson.com',
            'ext-redirectURI': 'https://q.wirelessplanning.emerson.com/en-us'
        }
    });

  const handleLogout = async () => {
    handleUserMenuClose();
      logout({ logoutParams: { returnTo: window.location.origin } });
  };

  return (
    <>
      <Box sx={{ paddingTop: "68px" }}>
        <AppBar
          position="fixed"
          color="transparent"
          elevation={0}
          sx={{
            boxShadow: "0px 4px 4px -2px rgba(0,0,0,0.2)",
            backgroundColor: "white",
          }}
        >
          <Container sx={{ maxWidth: { xs: "100%", md: "90%", lg: "75%", xl: "70%" } }}>
            <Toolbar
              sx={{
                display: "flex",
                justifyContent: "space-between",
                paddingLeft: "0px !important",
                paddingRight: "0px !important",
              }}
            >
              <Link href={`https://www.emerson.com/${selectedLanguageLocale}`} target="_blank">
                <img src="/assets/images/Corp_RGB_Standard_Digital.png" alt="Emerson" height="40px" />
              </Link>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: "16px" }}>
                {headerLinks.map((link, index) => (
                  <Button
                    key={index}
                    color="inherit"
                    component="a"
                    href={link.hyperlinkUrl || ""}
                    target="_blank"
                    sx={{
                      fontSize: "12px",
                      "&:hover": {
                        color: "secondary.main",
                        backgroundColor: "initial",
                      },
                    }}
                  >
                    <Typography variant="headermenu">{getString(link.labelDisplayTextKey)}</Typography>
                  </Button>
                ))}
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                <Button
                  disableRipple
                  color="inherit"
                  sx={{ "&:hover": { color: "secondary.main", backgroundColor: "initial" } }}
                  onClick={() => setIsLanguageDialogOpen(true)}
                >
                  <Stack direction="row" spacing={0} alignItems="center">
                    <span style={{ color: "inherit", paddingBottom: '0px' }}>
                      <GlobeIcon />
                    </span>
                    <Typography variant="headermenu" sx={{ ml: "4px", color: theme.palette.secondary.main }}>
                      {selectedLanguageLocale && formatLocale(selectedLanguageLocale)}
                    </Typography>
                  </Stack>
                </Button>
                              {isAuthenticatedUser && (
                  <>
                    <IconButton disableRipple edge="end" color="inherit" onClick={handleUserOptionClick}>
                      <Avatar
                        sx={{
                          width: "32px",
                          height: "32px",
                          fontSize: "16px",
                          bgcolor: "secondary.main",
                          "&:hover": { color: theme.accent.hover },
                        }}
                      >
                        {initials}
                      </Avatar>
                    </IconButton>
                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleUserMenuClose}>
                      <MenuItem
                        sx={{
                          cursor: "default",
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: "48px",
                            height: "48px",
                            fontSize: "24px",
                            bgcolor: "secondary.main",
                            mr: "16px",
                          }}
                        >
                          {initials}
                        </Avatar>
                        {getString("hdr_prof_greet_prefix")} {primaryName}
                      </MenuItem>
                      <Divider sx={{ bgcolor: "grey.800", ml: "16px", mr: "16px" }} />
                      <MenuItem onClick={handleLogout}>{getString("hdr_prof_logout")}</MenuItem>
                    </Menu>
                  </>
                )}
                             {!isAuthenticatedUser && (
                  <IconButton
                    disableRipple
                    aria-label={getString("hdr_prof_login")}
                    sx={{ "&:hover": { color: "secondary.main" } }}
                    onClick={handleLogin}
                  >
                    <PersonIcon />
                  </IconButton>
                )}
              </Box>
            </Toolbar>
          </Container>
        </AppBar>

        {/* Remove boxShadow in AppBar and uncomment this if want to prevent scrolling into the shadow
      <Box sx={{ height: "1px", boxShadow: 2}}>
      </Box>
      <div style={{height: '3px'}} >
      </div> */}
      </Box>
      <ChangeLanguageModal
        isOpen={isLanguageDialogOpen}
        setIsOpen={setIsLanguageDialogOpen}
        maxWidth="sm"
        handleClose={() => setIsLanguageDialogOpen(false)}
        handleConfirm={() => {
          setIsLanguageDialogOpen(false);
        }}
      />
    </>
  );
};

export default Header;
